import { useDebouncedValue, useQuery } from '@fv/client-core'
import type {
  BolNumberAvailabilityQuery,
  BolNumberAvailabilityResponse,
} from '@fv/models'

import { shipperFetch } from '../../utils/shipperFetch'
import { isDraftLoadId } from '../customer-routed/draftLoadId'

export type BolNumberAvailabilityState =
  | 'idle'
  | 'loading'
  | 'available'
  | 'used'

const bolNumberAvailabilityKeys = {
  all: ['bol-number-availability'] as const,
  check: (bolNumber: string, loadId?: string) =>
    [...bolNumberAvailabilityKeys.all, bolNumber, loadId] as const,
}

async function fetchBolNumberAvailability(
  query: BolNumberAvailabilityQuery,
): Promise<BolNumberAvailabilityResponse> {
  return await shipperFetch<BolNumberAvailabilityResponse>(
    '/shipments/bol-num/availability',
    { query },
  )
}

export const useBolNumAvailability = (
  bolNumber = '',
  loadId?: string,
): BolNumberAvailabilityState => {
  const debouncedBolNumber = useDebouncedValue(bolNumber.trim(), 500)
  const query = useQuery(
    bolNumberAvailabilityKeys.check(debouncedBolNumber, loadId),
    () =>
      fetchBolNumberAvailability({
        bolNumber: debouncedBolNumber,
        ...(loadId && !isDraftLoadId(loadId) && { loadId }),
      }),
    {
      enabled: debouncedBolNumber.length > 0,
    },
  )

  if (!debouncedBolNumber) return 'idle'
  if (query.isFetching) return 'loading'
  if (!query.data) return 'idle'
  if (query.data?.isUsed) return 'used'

  return 'available'
}
